import BaseController from "niteroi/packages/system/controllers/Base.controller";
import JSONModel from "sap/ui/model/json/JSONModel";
import {Button$PressEvent} from "sap/m/Button";
import Toolbar from "sap/m/Toolbar";
import HeaderContainer from "sap/m/HeaderContainer";
import Page from "sap/m/Page";
import View from "sap/ui/core/mvc/View";
import ComponentContainer from "sap/ui/core/ComponentContainer";
import Control from "sap/ui/core/Control";
import {Route$PatternMatchedEvent} from "sap/ui/core/routing/Route";
import {ILoadDataResponse} from "niteroi/packages/system/models/interfaces";

type TParams = {
    name: string,
    arguments: object,
    config: object,
    view: View | ComponentContainer,
    views: Array<View> | Array<ComponentContainer>,
    targetControl: Control,
    targetControls: Array<Control>
};

export default class Packages extends BaseController {
    private readonly onPatternMatchedHandler = this.onPatternMatched.bind(this);

    public onInit(): void {
        super.onInit();
        const router = this.getRouter();

        const route = router.getRoute("system/packages");
        route.detachPatternMatched(this.onPatternMatchedHandler);
        route.attachPatternMatched(this.onPatternMatchedHandler);
    }

    protected onPatternMatched(event: Route$PatternMatchedEvent): void {
        const params = event.getParameters() as TParams;
        const id = (params.arguments as { id: string }).id ?? "";
        this.getData(id);
    }

    protected getData(objectId: string = ""): void {
        this.getView().setBusy(true);
        const url = objectId ? `/api/packages/${objectId}` : "/api/packages";
        const modelName = objectId ? "package" : "packages";
        const model = this.getModel(modelName);
        model.loadData(url, "", true, "GET", false, true, this.getRequestHeaders()).then(() => {
            this.originalData = objectId ? Object.assign({}, JSON.parse(model.getJSON())) : {};
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
            model.setData(null);
        }).finally(() => {
            this.getView().setBusy(false);
        });
    }

    public onExit(): void {
        this.isModelSaved = true;
        this.originalData = {};
        const model = this.getModel("package");
        model.destroy();
    }

    public onPressButtonDiscardFormEdit(): void {
        this.discardChanges("package");
    }

    public onPressManagePackage(event: Button$PressEvent): void {
        const page = (((event.getSource().getParent() as Toolbar).getParent() as HeaderContainer).getParent() as Page);
        page.setBusy(true);

        const source = event.getSource();
        const bindingContext = source.getBindingContext("packages");
        const selectedPackage = bindingContext.getModel().getProperty(bindingContext.getPath());
        const packageId = selectedPackage.packageId;
        const installed = selectedPackage.installed;
        const request = this.createRequestModel();
        const sessionModel = this.getModel("session");
        const companiesId: string = sessionModel.getProperty("/company/companyId") as string;
        const url = `/api/companies/${companiesId}/packages`;
        const method = installed ? "DELETE" : "POST";
        const headers = this.getRequestHeaders();
        const data = JSON.stringify({
            companyPackageId: this.uuidv4(),
            packageId: packageId,
            companyId: companiesId,
            isActive: true,
            assignedBy: sessionModel.getProperty("/user/userId") as string
        });

        request.loadData(url, data, true, method, false, true, headers).then(() => {
            const msgKey = installed ? "msgPackageUninstalledSuccess" : "msgPackageInstalledSuccess";
            this.showToast(this.getText(msgKey));
            (bindingContext.getModel() as JSONModel).setProperty(bindingContext.getPath() + "/installed", !installed);

            const sessionRequest = this.createRequestModel();
            sessionRequest.loadData("/api/sessions", "", true, "GET", false, true, this.getRequestHeaders()).then(() => {
                sessionModel.setData(JSON.parse(sessionRequest.getJSON()));
            });
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
        }).finally(() => {
            page.setBusy(false);
        });
        page.setBusy(false);
    }
}
